// Time synchronization service with Vercel API endpoint
// Uses performance.now() for accurate elapsed time tracking
// Includes proper cleanup, source tracking, and smart sync gating

export class TimeService {
  static instance = null;
  
  constructor() {
    // Base values for time calculation
    this.baseServerUtcMs = null;
    this.basePerfMs = null;
    
    this.deviceTime = null;
    this.offset = 0;
    this.lastSync = null;
    this.source = 'device'; // Track actual source
    
    this.listeners = new Set();
    
    // Sync gating
    this.syncInProgress = false;
    this.sessionStartTime = Date.now();
    this.minSyncInterval = 30 * 60 * 1000; // 30 minutes minimum
    this.maxSyncInterval = 60 * 60 * 1000; // 60 minutes maximum
    
    // Timer IDs for proper cleanup
    this.driftIntervalId = null;
    this.initialTimeoutId = null;
    
    // Visibility API
    this.isTabVisible = typeof document !== 'undefined' ? !document.hidden : true;
    this.visibilityHandler = null;
    this.setupVisibilityListener();
    
    // Drift detection cooldown
    this.lastDriftResync = null;
    this.driftCooldown = 5 * 60 * 1000; // 5 minutes cooldown
  }

  static getInstance() {
    if (!TimeService.instance) {
      TimeService.instance = new TimeService();
    }
    return TimeService.instance;
  }

  // Setup visibility change listener with proper cleanup
  setupVisibilityListener() {
    if (typeof document === 'undefined') return;
    
    // Remove existing listener if any
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    
    this.visibilityHandler = () => {
      const wasHidden = !this.isTabVisible;
      this.isTabVisible = !document.hidden;
      
      // If tab becomes visible and it's been a while, consider re-syncing
      if (this.isTabVisible && wasHidden && this.shouldResync()) {
        this.sync({ reason: 'tab_visible' });
      }
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  // Determine if we should resync (respects tab visibility)
  shouldResync(force = false) {
    if (force) return true;
    
    // If tab is hidden, only resync if forced (e.g., large drift)
    if (!this.isTabVisible) {
      return false;
    }
    
    if (!this.lastSync) return true;
    
    const timeSinceSync = Date.now() - this.lastSync;
    const sessionAge = Date.now() - this.sessionStartTime;
    
    // Resync if:
    // 1. Never synced
    // 2. It's been >30 min since last sync
    // 3. Session has been open >60 min
    if (timeSinceSync > this.minSyncInterval) {
      return true;
    }
    
    if (sessionAge > this.maxSyncInterval) {
      return true;
    }
    
    return false;
  }

  // Add jitter to prevent thundering herd
  getJitteredDelay(baseDelay) {
    const jitter = Math.random() * 30000; // 0-30 seconds
    return baseDelay + jitter;
  }

  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback({
          serverTime: this.getCurrentTime(),
          deviceTime: new Date(),
          offset: this.offset,
          accuracy: this.getAccuracyStatus()
        });
      } catch (error) {
        console.error('Error in time service listener:', error);
      }
    });
  }

  // Fetch authoritative time from Vercel API endpoint
  // Optional: measure RTT for improved accuracy
  async fetchAuthoritativeTime() {
    const t0 = performance.now();
    
    try {
      const response = await fetch('/api/now', { 
        cache: 'no-store', // Disable all caching - time must always be fresh
        method: 'GET'
      });

      const t1 = performance.now();
      const rtt = t1 - t0;

      // Check if response is actually JSON (CRA doesn't serve API routes in dev, returns HTML)
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        // In development mode, API routes aren't available - use device time
        console.warn('Time API not available in development mode. Using device time.');
        const deviceTime = new Date();
        return {
          serverTime: deviceTime,
          source: 'device',
          deviceTime,
          rtt: 0
        };
      }

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.nowUtcMs || typeof data.nowUtcMs !== 'number') {
        throw new Error('Invalid response format');
      }

      const deviceTime = new Date();
      
      // Optional: Use RTT midpoint for improved accuracy
      // This accounts for network latency
      const estimatedServerTime = data.nowUtcMs + (rtt / 2);
      const serverTime = new Date(estimatedServerTime);

      return {
        serverTime,
        source: 'vercel-now',
        deviceTime,
        rtt
      };
    } catch (error) {
      // In development or if API fails, use device time gracefully
      console.warn('Time sync failed, using device time:', error.message);
      const deviceTime = new Date();
      return {
        serverTime: deviceTime,
        source: 'device',
        deviceTime,
        rtt: 0
      };
    }
  }

  // Sync with authoritative time source
  async sync(options = {}) {
    const { reason = 'manual', force = false } = options;

    // Gate: Don't sync if already in progress
    if (this.syncInProgress) {
      return this.getCachedResult();
    }

    // Gate: Don't sync too frequently unless forced
    if (!force && !this.shouldResync(force)) {
      return this.getCachedResult();
    }

    this.syncInProgress = true;

    try {
      const result = await this.fetchAuthoritativeTime();
      
      // fetchAuthoritativeTime always returns a valid result (server time or device time fallback)
      if (result && result.serverTime) {
        // Store base values for performance.now() calculation
        this.baseServerUtcMs = result.serverTime.getTime();
        this.basePerfMs = performance.now();
        
        this.deviceTime = result.deviceTime;
        this.offset = result.serverTime.getTime() - result.deviceTime.getTime();
        this.lastSync = Date.now();
        this.source = result.source; // Track source (vercel-now or device)

        if (result.source === 'vercel-now') {
          console.log(`Time synced (${reason}). Offset: ${this.offset}ms, RTT: ${result.rtt?.toFixed(2)}ms`);
        } else {
          console.log(`Using device time (${reason}). API not available in development mode.`);
        }
        
        // Notify listeners
        this.notifyListeners();
      }
    } catch (error) {
      // This should rarely happen since fetchAuthoritativeTime handles errors gracefully
      console.error('Unexpected time sync error:', error);
      
      // If we have a previous sync, continue using it
      if (this.baseServerUtcMs !== null) {
        console.warn('Using cached time sync due to error. Time remains accurate via performance.now()');
        return this.getCachedResult();
      }
      
      // First sync failed - use device time
      this.baseServerUtcMs = Date.now();
      this.basePerfMs = performance.now();
      this.deviceTime = new Date();
      this.offset = 0;
      this.lastSync = Date.now();
      this.source = 'device';
      
      console.warn('First sync failed. Falling back to device time.');
      this.notifyListeners();
    } finally {
      this.syncInProgress = false;
    }

    return this.getCachedResult();
  }

  // Get cached result for current state
  getCachedResult() {
    if (this.baseServerUtcMs === null) {
      return null;
    }

    return {
      serverTime: this.getCurrentTime(),
      deviceTime: new Date(),
      offset: this.offset,
      source: this.source, // Use tracked source, not hardcoded
      accuracy: this.getAccuracyStatus()
    };
  }

  // Get current synchronized time using performance.now()
  getCurrentTime() {
    if (this.baseServerUtcMs === null || this.basePerfMs === null) {
      // Not synced yet, return device time
      return new Date();
    }

    // Calculate elapsed time using performance.now() (monotonic, high precision)
    // This maintains accuracy even if Vercel is temporarily down
    const elapsed = performance.now() - this.basePerfMs;
    
    // Return server time + elapsed time
    return new Date(this.baseServerUtcMs + elapsed);
  }

  // Get accuracy status with improved drift thresholds
  getAccuracyStatus() {
    if (!this.lastSync) {
      return {
        status: 'not_synced',
        warning: 'Time not yet synchronized'
      };
    }

    const driftSeconds = Math.abs(this.offset) / 1000;
    const syncAge = (Date.now() - this.lastSync) / 1000;

    // Warn at 30-60 seconds, resync at larger threshold (with cooldown)
    // This prevents hammering the API on VMs or machines that drift
    if (driftSeconds > 60) {
      // Check cooldown before suggesting resync
      const canResync = !this.lastDriftResync || 
        (Date.now() - this.lastDriftResync) > this.driftCooldown;
      
      return {
        status: 'inaccurate',
        warning: `Device time is ${Math.abs(Math.round(driftSeconds))} seconds ${this.offset > 0 ? 'behind' : 'ahead'} of server time`,
        driftSeconds: Math.round(driftSeconds),
        offset: this.offset,
        shouldResync: canResync
      };
    } else if (driftSeconds > 30) {
      // Warning but don't force resync yet
      return {
        status: 'warning',
        warning: `Device time is ${Math.abs(Math.round(driftSeconds))} seconds ${this.offset > 0 ? 'behind' : 'ahead'} of server time`,
        driftSeconds: Math.round(driftSeconds),
        offset: this.offset,
        shouldResync: false
      };
    }

    // Check if sync is stale
    if (syncAge > 60 * 60) {
      return {
        status: 'stale',
        warning: 'Time sync is stale. Re-syncing...',
        syncAge: Math.round(syncAge),
        shouldResync: true
      };
    }

    return {
      status: 'accurate',
      warning: null,
      driftSeconds: Math.round(driftSeconds)
    };
  }

  // Start auto-sync with smart gating and proper timer tracking
  startAutoSync() {
    // Clear any existing timers first
    this.stopAutoSync();

    // Initial sync on load (with jitter to prevent thundering herd)
    const initialDelay = this.getJitteredDelay(1000); // 1-31 seconds
    this.initialTimeoutId = setTimeout(() => {
      this.sync({ reason: 'initial_load' });
      this.initialTimeoutId = null;
    }, initialDelay);

    // Periodic check for drift (every 5 minutes, but gated)
    this.driftIntervalId = setInterval(() => {
      const accuracy = this.getAccuracyStatus();
      
      // Auto-resync if large drift detected (respects cooldown)
      if (accuracy.shouldResync) {
        const delay = this.getJitteredDelay(0);
        setTimeout(() => {
          this.sync({ reason: 'drift_detected', force: true });
          this.lastDriftResync = Date.now();
        }, delay);
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
  }

  // Stop auto-sync and clean up all timers
  stopAutoSync() {
    if (this.initialTimeoutId) {
      clearTimeout(this.initialTimeoutId);
      this.initialTimeoutId = null;
    }
    
    if (this.driftIntervalId) {
      clearInterval(this.driftIntervalId);
      this.driftIntervalId = null;
    }
  }

  // Cleanup method for complete teardown
  destroy() {
    this.stopAutoSync();
    
    // Remove visibility listener
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    
    // Clear listeners
    this.listeners.clear();
  }

  // Get time info for display
  getTimeInfo() {
    return {
      currentTime: this.getCurrentTime(),
      deviceTime: new Date(),
      offset: this.offset,
      lastSync: this.lastSync,
      accuracy: this.getAccuracyStatus(),
      source: this.source
    };
  }
}

// Export singleton instance
export default TimeService.getInstance();

